<template>
    <v-card>
        <v-layout>
            <v-flex xs12 class="notPrinted">
                <v-layout>
                    <v-flex xs12>
                        <h1 class="text-xs-center">{{ title }}</h1>
                    </v-flex>
                </v-layout>
                <v-form v-model="searchFormValid" v-if="manualSearch">
                    <v-layout wrap>
                        <v-flex xs12 sm2 offset-sm1>
                            <v-menu
                                ref="datePickerMenu"
                                :close-on-content-click="false"
                                v-model="datePickerMenu"
                                :nudge-right="40"
                                lazy
                                transition="scale-transition"
                                offset-y
                                full-width
                                min-width="290px">
                                <v-text-field
                                    clearable
                                    slot="activator"
                                    v-model="search.date"
                                    label="Order Date"
                                    prepend-icon="event"
                                    hint="Order Date"
                                    readonly
                                ></v-text-field>
                                <v-date-picker 
                                    :allowed-dates="allowedDates"
                                    v-model="search.date"
                                    @input="datePickerMenu = false">
                                </v-date-picker>
                            </v-menu>
                        </v-flex>
                        <v-flex xs12 sm2 offset-sm1>
                            <v-autocomplete
                                :items="stores"
                                item-text="Name"
                                item-value="ID"
                                v-model="search.storeIDs"
                                multiple
                                label="Stores"
                                hint="Stores"
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm2 offset-sm1>
                            <v-autocomplete
                                :items="suppliers"
                                item-text="Name"
                                item-value="ID"
                                v-model="search.supplierIDs"
                                multiple
                                label="Suppliers"
                                hint="Suppliers"
                            ></v-autocomplete>
                        </v-flex>
                        <v-flex xs12 sm2 offset-sm1>
                            <v-btn block color="success" :disabled="!searchFormValid" :loading="loading" @click="loadOrders">Search</v-btn>
                        </v-flex>
                    </v-layout>
                </v-form>
                <v-layout>
                    <v-flex xs12>
                        <v-data-table
                            :headers="headers"
                            :items="orders"
                            :loading="loading"
                            must-sort
                            :rows-per-page-items="rowsPerPage"
                            :pagination.sync="pagination"
                            :total-items="totalOrders"
                        >
                            <v-progress-linear slot="progress" color="primary" indeterminate></v-progress-linear>
                            <template slot="items" slot-scope="props">
                                <tr :data-id="props.item.ID">
                                    <td>{{props.item.ID}}</td>
                                    <td>{{props.item.StoreName}}</td>
                                    <td>{{props.item.DepartmentName}}</td>
                                    <td>{{formatDate(props.item.Date)}}</td>
                                    <td>{{props.item.Suppliers}}</td>
                                    <td v-if="[0, 1].some(stage => stage == props.item.Stage)">
                                        <v-icon color="error">close</v-icon>
                                    </td>
                                    <td v-if="[2, 3].some(stage => stage == props.item.Stage)">
                                        <v-icon color="success">check</v-icon>
                                    </td>
                                    <td v-if="[0, 1, 2].some(stage => stage == props.item.Stage)">
                                        <v-icon color="error">close</v-icon>
                                    </td>
                                    <td v-if="[3].some(stage => stage == props.item.Stage)">
                                        <v-icon color="success">check</v-icon>
                                    </td>
                                    <td v-if="isEditable(props.item)">
                                        <v-btn
                                            color="success"
                                            block
                                            @click="editOrder(props.item)"
                                        >
                                            Continue Order
                                        </v-btn>
                                    </td>
                                    <td v-if="isNotEditable(props.item)">
                                        <v-btn
                                            color="info"
                                            block
                                            @click="viewDetailsOrder(props.item)"
                                        >
                                            View Details
                                        </v-btn>
                                    </td>
                                </tr>
                            </template>
                            <template v-if="!loading" slot="no-data">
                                <v-alert v-if="loadError" outline :value="true" color="error" icon="error">
                                    {{loadError}}
                                </v-alert>
                                <v-alert v-if="!loadError" outline :value="true" type="info">
                                    No results found
                                </v-alert>
                            </template>
                        </v-data-table>
                    </v-flex>
                </v-layout>
            </v-flex>
        </v-layout>
    </v-card>
</template>

<script>
import validationRules from "../../helpers/validationrules";
import auth from '@/services/auth';
import util from '@/services/util';
import PrintableOrderSummary from './PrintableOrderSummary';

export default {
    components: {
        PrintableOrderSummary
    },
    props: [
        'title',
        'search',
        'manualSearch',
        'startingRowsPerPage',
        'startingDescending'
    ],
    data() {
        return {
            ordersLoading: false,
            orders: [],
            totalOrders: 0,
            rules: validationRules.rules,
            datePickerMenu: false,
            searchFormValid: false,
            infoVisible: false,
            rowsPerPage: [5, 20, 50, 100],
            pagination: {
                rowsPerPage: 20, // default
                sortBy: "Date",
                descending: true
            },
            headers: [
                {
                    text: "Order Number",
                    value: "ID"
                },
                {
                    text: "Store",
                    value: "StoreName"
                },
                {
                    text: "Department",
                    value: "DepartmentName"
                },
                {
                    text: "Date",
                    value: "Date"
                },
                {
                    text: "Suppliers",
                    value: "Suppliers"
                },
                {
                    text: "Confirmed",
                    sortable: false
                },
                {
                    text: "Sent",
                    sortable: false
                },
                {
                    text: "", // view button
                    sortable: false
                }
            ]
        };
    },
    watch: {
        pagination: {
            handler(){
                this.loadOrders();
            },
            deep: true
        }
    },
    computed: {
        loading() {
            return this.ordersLoading
                || this.$store.state.orderStore.products.loading
                || this.$store.state.publicHolidayStore.loading
                || this.$store.state.storeStore.loading
                || this.$store.state.supplierStore.loading;
        },
        loadError() {
            return this.$store.state.orderStore.loadError
                || this.$store.state.orderStore.products.loadError;
        },
        publicHolidays(){
            return this.$store.state.publicHolidayStore.publicHolidays;
        },
        stores() {
            return this.$store.state.storeStore.stores.filter(s => s.Active && auth.hasAccessToStore(s.ID)).sort(util.sortByName);
        },
        suppliers(){
            return this.$store.state.supplierStore.suppliers.filter(c => c.Active);
        }
    },
    methods: {
        loadOrders() {
            this.ordersLoading = true;

            let that = this;
            this.$store.dispatch("orderStore/loadOrders", {
                params: {
                    PaginationPage: this.pagination.page,
                    PaginationPageSize: this.pagination.rowsPerPage,
                    SortBy: this.pagination.sortBy,
                    SortDirection: this.pagination.descending ? "DESC" : "ASC",
                    SearchDate: this.search.date,
                    SearchMinDate: this.search.minDate,
                    SearchMaxDate: this.search.maxDate,
                    SearchStages: JSON.stringify(this.search.stages),
                    SearchStoreIDs: JSON.stringify(this.search.storeIDs),
                    SearchSupplierIDs: JSON.stringify(this.search.supplierIDs)
                },
                success: (data) => {
                    that.orders = data.Orders;
                    that.totalOrders = data.TotalOrders;
                },
                finally: () => {
                    that.ordersLoading = false;
                }
            });
        },
        isEditable(item){
            return [0, 1, 2].some(stage => stage == item.Stage);
        },
        isNotEditable(item){
            return [3, 4].some(stage => stage == item.Stage);
        },
        editOrder(item){
            this.$router.push({
                name: 'editorder',
                params: {
                    editOrderID: item.ID
                }
            });
        },
        viewDetailsOrder(item) {
            this.$router.push({
                name: "vieworderdetails",
                params: {
                    orderID: item.ID,
                    backRouteName: this.$router.history.current.name
                }
            })
        },
        formatDate(dateString){
            let date = new Date(dateString);

            let day = date.getDate() + "";
            if(day.length == 1){
                day = "0" + day;
            }
            let month = date.getMonth() + 1 + "";
            if(month.length == 1){
                month = "0" + month;
            }
            let year = date.getFullYear();

            return day + "/" + month + "/" + year;
        },
        formatStage(stage){
            if(stage == 0 || stage == 1){
                return "Draft";
            }

            if(stage == 2){
                return "Confirmed";
            }

            if(stage == 3){
                return "Sent";
            }

            if(stage == 4){
                return "Complete";
            }
        },
        allowedDates(val){
            var date = new Date(val);

            let dateIsPublicHoliday = this.publicHolidays.find(pH => {
                let publicHoliday = new Date(pH.Date);

                if(publicHoliday.getDate() == date.getDate()
                    && publicHoliday.getMonth() == date.getMonth()
                    && publicHoliday.getFullYear() == date.getFullYear()){
                    return true;
                }
            });
            if(dateIsPublicHoliday){
                return false;
            }

            return true;
        },
    },
    created(){
        if(!this.search){
            this.search = {};
        }

        if(!this.search.date){
            this.search.date = "";
        }

        if(!this.search.minDate){
            this.search.minDate = "";
        }

        if(!this.search.maxDate){
            this.search.maxDate = "";
        }

        if(!this.search.stages){
            this.search.stages = [];
        }

        if(!this.search.storeIDs){
            this.search.storeIDs = [];
        }

        if(!this.search.supplierIDs){
            this.search.supplierIDs = [];
        }

        if(this.startingRowsPerPage){
            this.pagination.rowsPerPage = this.startingRowsPerPage;
        }

        if(this.startingDescending != null){
            this.pagination.descending = this.startingDescending;
        }
    }
};
</script>

<style>

</style>